import {
  getMarkets,
  getUnreviewedMarketReferences,
  updateMarket,
  createMarket,
  deleteMarket,
} from 'common/src/api/markets';
import { Market } from 'common/src/utils/types';
import { getCurrencies } from 'common/src/api/currencies';
import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Select,
  SelectItem,
  Switch,
  Card,
  CardBody,
  Skeleton,
  Chip,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import CustomAutocomplete from '../../../components/common/CustomAutocomplete';
import Header from '../../../components/layouts/Header';
import { Alert, ConfirmModal, PageLayout } from '../../../components/ui';

const normalizeCountryCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);

const countryCodeFromLocode = (locode: string) =>
  normalizeCountryCode(locode.slice(0, 2));

const SuperadminMarkets = () => {
  const [markets, setMarkets] = useState<Market[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreviewedRefs, setUnreviewedRefs] = useState<
    { table_name: string; market_id: string; market_name: string; total: number }[]
  >([]);
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [newMarket, setNewMarket] = useState<{
    id: string;
    name: string;
    country: string;
    country_code: string;
    primary_market_id: string | null;
    reviewed: boolean;
    default_currency: string | null;
  }>({
    id: '',
    name: '',
    country: '',
    country_code: '',
    primary_market_id: null,
    reviewed: false,
    default_currency: null,
  });
  const [currencies, setCurrencies] = useState<string[]>([]);
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isNewOpen,
    onOpen: onNewOpen,
    onClose: onNewClose,
  } = useDisclosure();

  const fetchMarkets = async () => {
    setLoading(true);
    try {
      const [fetchedMarkets, refs] = await Promise.all([
        getMarkets(),
        getUnreviewedMarketReferences(),
      ]);
      setMarkets(fetchedMarkets);
      setUnreviewedRefs(refs);
    } catch (error) {
      console.error('Error fetching markets:', error);
      toast.error('Failed to fetch markets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarkets();
    getCurrencies()
      .then((list) => setCurrencies(list.map((c) => c.value)))
      .catch(() => setCurrencies([]));
  }, []);

  const primaryMarketOptions = useMemo(
    () =>
      markets
        .filter((market) => market.id !== selectedMarket?.id)
        .slice()
        .sort((a, b) => {
          const countryCompare = (a.country || '').localeCompare(
            b.country || ''
          );
          if (countryCompare !== 0) return countryCompare;
          return (a.name || '').localeCompare(b.name || '');
        })
        .map((market) => ({
          key: market.id,
          value: market.id,
          label: `${market.id} - ${market.name} - ${market.country}`,
        })),
    [markets, selectedMarket?.id]
  );

  const handleEdit = (market: Market) => {
    setSelectedMarket(market);
    onEditOpen();
  };

  const handleDelete = (market: Market) => {
    setSelectedMarket(market);
    onDeleteOpen();
  };

  const handleEditSubmit = async () => {
    if (!selectedMarket) return;
    try {
      const updateData = { ...selectedMarket };
      delete updateData.effective_market_id;
      await updateMarket(selectedMarket.id, updateData);
      toast.success('Market updated successfully');
      onEditClose();
      fetchMarkets();
    } catch (error) {
      console.error('Error updating market:', error);
      toast.error('Failed to update market');
    }
  };

  const handleNewSubmit = async () => {
    try {
      if (newMarket.id.length < 2 || newMarket.id.length > 5) {
        toast.error('El ID del mercado debe tener entre 2 y 5 caracteres');
        return;
      } else if (!newMarket.name) {
        toast.error('El nombre del mercado es requerido');
        return;
      } else if (!newMarket.country) {
        toast.error('El país del mercado es requerido');
        return;
      }
      await createMarket(newMarket);
      toast.success('New market created successfully');
      onNewClose();
      setNewMarket({
        id: '',
        name: '',
        country: '',
        country_code: '',
        primary_market_id: null,
        reviewed: false,
        default_currency: null,
      });
      fetchMarkets();
    } catch (error) {
      console.error('Error creating market:', error);
      toast.error('Failed to create new market');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMarket) return;
    try {
      await deleteMarket(selectedMarket.id);
      toast.success('Market deleted successfully');
      onDeleteClose();
      fetchMarkets();
    } catch (error) {
      console.error('Error deleting market:', error);
      toast.error('Failed to delete market');
    }
  };

  const columns = [
    { name: 'Locode', uid: 'id' },
    { name: 'Nombre', uid: 'name' },
    { name: 'País', uid: 'country' },
    { name: 'Código País', uid: 'country_code' },
    { name: 'Mercado Primario', uid: 'primary_market_id' },
    { name: 'Moneda', uid: 'default_currency' },
    { name: 'Revisado', uid: 'reviewed' },
    { name: 'Acciones', uid: 'actions' },
  ];

  const renderCell = (market: Market, columnKey: React.Key) => {
    switch (columnKey) {
      case 'primary_market_id':
        return market.primary_market_id || '-';
      case 'default_currency':
        return market.default_currency || '-';
      case 'reviewed':
        return (
          <Chip
            color={market.reviewed ? 'success' : 'default'}
            size='sm'
            variant='flat'
          >
            {market.reviewed ? 'Sí' : 'No'}
          </Chip>
        );
      case 'actions':
        return (
          <div className='flex items-center gap-2'>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              onClick={() => handleEdit(market)}
            >
              <Icon icon='tabler:edit' className='text-default-400' />
            </Button>
            <Button
              isIconOnly
              size='sm'
              variant='light'
              color='danger'
              onClick={() => handleDelete(market)}
            >
              <Icon icon='tabler:trash' className='text-danger-400' />
            </Button>
          </div>
        );
      default:
        return market[columnKey as keyof Market];
    }
  };

  return (
    <PageLayout>
      <Header
        title='Mercados'
        actions={
          <Button color='primary' onClick={onNewOpen}>
            Añadir Mercado
          </Button>
        }
      />

      {unreviewedRefs.length > 0 && (
        <Alert variant='warning' showIcon className='mb-4'>
          <p className='font-semibold mb-2'>
            Hay referencias a mercados no revisados
          </p>
          <ul className='text-sm font-normal list-disc pl-4'>
            {unreviewedRefs.map((ref, i) => (
              <li key={i}>
                <span className='font-medium'>{ref.market_name}</span> ({ref.market_id}) — {ref.total} en {ref.table_name}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {loading ? (
        <div className=''>
          <div className='flex justify-between items-center '>
            <div className='space-y-2'>
              <Skeleton className='w-1/3 rounded-lg' />
            </div>
          </div>

          <Card className='w-full'>
            <CardBody>
              <div className='space-y-4'>
                <div className='grid grid-cols-6 gap-4'>
                  {[...Array(6)].map((_, i) => (
                    <Skeleton key={i} className='h-8 w-full rounded-lg' />
                  ))}
                </div>

                {[...Array(5)].map((_, rowIndex) => (
                  <div key={rowIndex} className='grid grid-cols-6 gap-4'>
                    <Skeleton className='h-12 w-16 rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-full rounded-lg' />
                    <Skeleton className='h-12 w-24 rounded-lg mx-auto' />
                    <div className='flex gap-2 justify-center'>
                      <Skeleton className='h-8 w-8 rounded-lg' />
                      <Skeleton className='h-8 w-8 rounded-lg' />
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>
      ) : (
        <Table removeWrapper aria-label='Markets table'>
          <TableHeader columns={columns}>
            {(column) => (
              <TableColumn key={column.uid}>{column.name}</TableColumn>
            )}
          </TableHeader>
          <TableBody items={markets}>
            {(item) => (
              <TableRow key={item.id}>
                {(columnKey) => (
                  <TableCell>{renderCell(item, columnKey)}</TableCell>
                )}
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Modal isOpen={isEditOpen} onClose={onEditClose}>
        <ModalContent>
          <ModalHeader>Editar Mercado</ModalHeader>
          <ModalBody>
            <Input
              label='ID'
              value={selectedMarket?.id || ''}
              onChange={(e) =>
                setSelectedMarket({
                  ...selectedMarket!,
                  id: e.target.value.toUpperCase(),
                })
              }
            />
            <Input
              label='Código País'
              value={selectedMarket?.country_code || ''}
              onChange={(e) =>
                setSelectedMarket({
                  ...selectedMarket!,
                  country_code: normalizeCountryCode(e.target.value),
                })
              }
              description='ISO 3166-1 alpha-2, ej: CL, CN, US'
            />
            <Input
              label='Nombre'
              value={selectedMarket?.name || ''}
              onChange={(e) =>
                setSelectedMarket({
                  ...selectedMarket!,
                  name: e.target.value.toUpperCase(),
                })
              }
            />
            <Input
              label='País'
              value={selectedMarket?.country || ''}
              onChange={(e) =>
                setSelectedMarket({
                  ...selectedMarket!,
                  country: e.target.value.toUpperCase(),
                })
              }
            />
            <div>
              <CustomAutocomplete
                label='Mercado Primario'
                placeholder='Buscar mercado primario...'
                options={primaryMarketOptions}
                selectedKey={selectedMarket?.primary_market_id || undefined}
                onSelectionChange={(key) =>
                  setSelectedMarket({
                    ...selectedMarket!,
                    primary_market_id: key ? String(key) : null,
                  })
                }
              />
            </div>
            <Select
              label='Moneda por defecto'
              selectedKeys={selectedMarket?.default_currency ? [selectedMarket?.default_currency] : []}
              onSelectionChange={(keys) =>
                setSelectedMarket({
                  ...selectedMarket!,
                  default_currency: (Array.from(keys)[0] as string) ?? null,
                })
              }
              description='Con la que parte Registrar precios en este mercado'
            >
              {currencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </Select>
            <div className='flex items-center gap-2'>
              <Switch
                isSelected={selectedMarket?.reviewed ?? false}
                onValueChange={(val) =>
                  setSelectedMarket({ ...selectedMarket!, reviewed: val })
                }
              />
              <span>Revisado</span>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleEditSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title='Confirmar Eliminación'
        confirmLabel='Eliminar'
        cancelLabel='Cancel'
        isDestructive
      >
        ¿Estás seguro de que quieres eliminar {selectedMarket?.name}?
      </ConfirmModal>

      <Modal isOpen={isNewOpen} onClose={onNewClose}>
        <ModalContent>
          <ModalHeader>Nuevo Mercado</ModalHeader>
          <ModalBody>
            <Input
              label='Locode'
              value={newMarket.id}
              onChange={(e) => {
                const id = e.target.value.toUpperCase();
                setNewMarket({
                  ...newMarket,
                  id,
                  country_code: countryCodeFromLocode(id),
                });
              }}
              description='El Locode del mercado (2 a 5 caracteres)'
            />
            <Input
              label='Código País'
              value={newMarket.country_code}
              onChange={(e) =>
                setNewMarket({
                  ...newMarket,
                  country_code: normalizeCountryCode(e.target.value),
                })
              }
              description='Se completa automáticamente desde el Locode'
            />
            <Input
              label='Nombre'
              value={newMarket.name}
              onChange={(e) =>
                setNewMarket({
                  ...newMarket,
                  name: e.target.value.toUpperCase(),
                })
              }
              description='Ciudad'
            />
            <Input
              label='País'
              value={newMarket.country}
              onChange={(e) =>
                setNewMarket({
                  ...newMarket,
                  country: e.target.value.toUpperCase(),
                })
              }
              description='País (en inglés)'
            />
            <div>
              <label className='block text-sm font-medium text-default-600 mb-1'>
                Mercado Primario
              </label>
              <select
                className='w-full rounded-lg border border-default-200 bg-default-100 px-3 py-2 text-sm outline-none focus:border-primary'
                value={newMarket.primary_market_id || ''}
                onChange={(e) =>
                  setNewMarket({
                    ...newMarket,
                    primary_market_id: e.target.value || null,
                  })
                }
              >
                <option value=''>Sin mercado primario</option>
                {markets.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.id} - {m.name}
                  </option>
                ))}
              </select>
              <p className='text-xs text-default-400 mt-1'>
                Opcional - mercado principal al que pertenece
              </p>
            </div>
            <Select
              label='Moneda por defecto'
              selectedKeys={newMarket.default_currency ? [newMarket.default_currency] : []}
              onSelectionChange={(keys) =>
                setNewMarket({
                  ...newMarket,
                  default_currency: (Array.from(keys)[0] as string) ?? null,
                })
              }
              description='Con la que parte Registrar precios en este mercado'
            >
              {currencies.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </Select>
            <div className='flex items-center gap-2'>
              <Switch
                isSelected={newMarket.reviewed}
                onValueChange={(val) =>
                  setNewMarket({ ...newMarket, reviewed: val })
                }
              />
              <span>Revisado</span>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onClick={onNewClose}>
              Cancel
            </Button>
            <Button
              color='primary'
              onClick={handleNewSubmit}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
};

export default SuperadminMarkets;
